import { makeAutoObservable, runInAction } from "mobx";

import {
  notionConnectionStatus,
  notionDisconnect,
  fetchNotionPages,
  importNotionPages,
} from "@/api";

import type { NotionPage } from "@/api";
import type { RootStore } from "@/store/RootStore";

type ImportTarget = {
  parentUid: string;
  position: number;
};

const NOTION_AUTHORIZE_URL = "https://api.notion.com/v1/oauth/authorize";
export const NOTION_OAUTH_STATE_KEY = "notion_oauth_state";
export const NOTION_OAUTH_SUCCESS_REDIRECT_KEY = "notion_oauth_success_redirect";

export const getNotionOauthRedirectUri = () => `${window.location.origin}/oauth/notion/callback`;

export class ImportFromNotionDialogStore {
  rootStore: RootStore;

  isOpen = false;

  status: "idle" | "loading" | "connect" | "picker" | "importing" = "idle";

  workspaceName = "";

  pages: NotionPage[] = [];

  selectedIds: Set<string> = new Set();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  open = () => {
    this.isOpen = true;
  };

  close = () => {
    this.isOpen = false;
  };

  reset = () => {
    this.status = "idle";
    this.pages = [];
    this.selectedIds = new Set();
  };

  toggleSelection = (id: string) => {
    const next = new Set(this.selectedIds);

    const collectSubtree = (nodes: NotionPage[], targetId: string): string[] => {
      for (const node of nodes) {
        if (node.id === targetId) {
          const ids: string[] = [node.id];
          const walk = (n: NotionPage) => {
            for (const child of n.children ?? []) {
              ids.push(child.id);
              walk(child);
            }
          };
          walk(node);
          return ids;
        }
        const found = collectSubtree(node.children ?? [], targetId);
        if (found.length) return found;
      }
      return [];
    };

    const ids = collectSubtree(this.pages, id);
    if (next.has(id)) {
      for (const i of ids) next.delete(i);
    } else {
      for (const i of ids) next.add(i);
    }
    this.selectedIds = next;
  };

  selectAll = () => {
    const allIds = new Set<string>();
    const collect = (nodes: NotionPage[]) => {
      for (const n of nodes) {
        allIds.add(n.id);
        if (n.children?.length) collect(n.children);
      }
    };
    collect(this.pages);

    const isAllSelected = allIds.size > 0 && this.selectedIds.size === allIds.size;
    this.selectedIds = isAllSelected ? new Set() : allIds;
  };

  initialize = async () => {
    if (!this.isOpen) return;

    if (!this.rootStore.auth.isLoggedIn) {
      this.status = "connect";
      return;
    }

    this.status = "loading";
    await this.loadConnectionStatus();
  };

  private async loadConnectionStatus() {
    try {
      const { connected, workspace_name } = await notionConnectionStatus();
      if (connected) {
        runInAction(() => {
          this.status = "loading";
          this.workspaceName = workspace_name || "";
        });
        const pagesRes = await fetchNotionPages();
        runInAction(() => {
          this.pages = pagesRes.pages || [];
          const allIds = new Set<string>();
          const collect = (nodes: NotionPage[]) => {
            for (const n of nodes) {
              allIds.add(n.id);
              if (n.children?.length) collect(n.children);
            }
          };
          collect(this.pages);
          this.selectedIds = allIds;
          this.status = "picker";
        });
      } else {
        runInAction(() => {
          this.status = "connect";
          this.workspaceName = "";
        });
      }
    } catch {
      runInAction(() => {
        this.status = "connect";
      });
    }
  }

  connect = (successUrl: string) => {
    if (!this.rootStore.auth.isLoggedIn) {
      this.rootStore.dialog.loginDialog.open();
      return;
    }

    const clientId = process.env.NEXT_PUBLIC_NOTION_CLIENT_ID;
    if (!clientId) {
      console.error("NEXT_PUBLIC_NOTION_CLIENT_ID is not configured");
      return;
    }

    const state = crypto.randomUUID();
    sessionStorage.setItem(NOTION_OAUTH_STATE_KEY, state);
    sessionStorage.setItem(NOTION_OAUTH_SUCCESS_REDIRECT_KEY, successUrl);

    const url = new URL(NOTION_AUTHORIZE_URL);
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("owner", "user");
    url.searchParams.set("redirect_uri", getNotionOauthRedirectUri());
    url.searchParams.set("state", state);

    window.location.href = url.toString();
  };

  disconnect = async () => {
    // Switch UI to "connect" immediately; for testing we don't block on the response.
    this.status = "connect";
    this.pages = [];
    this.selectedIds = new Set();

    try {
      await notionDisconnect();
    } catch {
      // ignore
    }
  };

  importToTarget = (target: ImportTarget) => {
    if (this.selectedIds.size === 0) return;

    const pageIds = Array.from(this.selectedIds);
    importNotionPages({
      pageIds,
      parentUid: target.parentUid,
      position: target.position,
    });
  };
}
